import * as Tabs from "@radix-ui/react-tabs";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatDuration } from "../../utils";
import { DurationTrendChart } from "./DurationAnalyticsSection";
import DurationHistogram, { type DurationRange } from "./DurationHistogram";
import type { CaseAnalysis, DurationProfile } from "./processCaseNotes";
import { durationAnalytics, hasHighDurationVariability } from "./processDurationStats";
import { type ProcessInstance, durationObservationsForInstances } from "./processGraphModel";
import { PanelSection } from "./processSidebarUi";

export default function ProcessCaseCharts({
  analysis,
  reference,
  referenceInstances,
  durationRange,
  onDurationRange,
  onAskVariability,
  onReviewOutliers,
}: {
  analysis: CaseAnalysis;
  /** Same scope, before duration filtering, so the benchmark cannot move on selection. */
  reference: DurationProfile;
  referenceInstances: ProcessInstance[];
  durationRange: DurationRange | null;
  onDurationRange: (range: DurationRange | null) => void;
  onAskVariability?: () => void;
  onReviewOutliers: () => void;
}) {
  const { t } = useTranslation();
  const [chartView, setChartView] = useState("distribution");
  const { cases } = analysis;
  const durations = useMemo(() => cases.map(reading => reading.duration), [cases]);
  const trend = useMemo(
    () =>
      durationAnalytics(durationObservationsForInstances(cases.map(reading => reading.instance)))
        .trend,
    [cases],
  );
  const referenceTrend = useMemo(
    () => durationAnalytics(durationObservationsForInstances(referenceInstances)).trend,
    [referenceInstances],
  );
  // Keep the count on the same pre-duration-filter scope as the threshold.
  const referenceCases = referenceInstances.filter(instance =>
    Number.isFinite(instance.source.avgDuration),
  );
  const outliers = referenceCases.filter(
    instance =>
      reference.outlierAbove !== null && instance.source.avgDuration > reference.outlierAbove,
  );
  const outlierMin = Math.min(...outliers.map(instance => instance.source.avgDuration));
  const reviewingOutliers =
    durationRange?.min === outlierMin && durationRange.max === Number.POSITIVE_INFINITY;
  if (!reference.count && !durationRange) return null;
  const tabClass =
    "min-w-0 flex-1 rounded-md px-2 py-1.5 text-xs font-medium text-text-dim outline-none data-[state=active]:bg-surface data-[state=active]:text-text data-[state=active]:shadow-sm focus-visible:ring-2 focus-visible:ring-info/30";
  return (
    <PanelSection title={t("processPanel.caseDuration")} help={t("processPanel.caseWorkTimeHelp")}>
      <Tabs.Root value={chartView} onValueChange={setChartView}>
        <Tabs.List
          aria-label={t("processPanel.caseCharts")}
          className="mb-2 flex gap-1 rounded-lg bg-surface-strong p-1"
        >
          <Tabs.Trigger value="distribution" className={tabClass}>
            {t("processPanel.caseDistribution")}
          </Tabs.Trigger>
          <Tabs.Trigger value="trend" className={tabClass}>
            {t("trend_over_time")}
          </Tabs.Trigger>
        </Tabs.List>
        {/* Both panels size the same grid row, including wrapped findings and chart controls. */}
        <div className="grid">
          <Tabs.Content
            forceMount
            value="distribution"
            className="col-start-1 row-start-1 self-start outline-none"
            style={{ visibility: chartView === "distribution" ? "visible" : "hidden" }}
            aria-hidden={chartView !== "distribution"}
          >
            <DurationHistogram
              values={durations}
              height={150}
              countLabel={t("instances")}
              title={false}
              description={t("processPanel.caseDistributionDescription")}
              outlierAbove={reference.outlierAbove}
              selection={durationRange}
              showSelectionSummary={!reviewingOutliers}
              onSelectionChange={onDurationRange}
              onAskVariability={
                onAskVariability && hasHighDurationVariability(durations)
                  ? onAskVariability
                  : undefined
              }
            />
            {outliers.length > 0 && reference.outlierAbove !== null && (
              <output
                aria-label={t("processPanel.unusuallyLongCases")}
                className="mt-2 block text-xs leading-relaxed text-text-mid"
              >
                <strong className="font-semibold text-text">
                  {t("processPanel.outliersCount", {
                    count: outliers.length,
                    share: Math.round((outliers.length / reference.count) * 100),
                  })}
                </strong>{" "}
                {t("processPanel.outliersDetailPrefix")}{" "}
                <strong className="font-semibold text-text">
                  {formatDuration(reference.outlierAbove)}
                </strong>{" "}
                {t("processPanel.outliersDetailSuffix")}.{" "}
                <button
                  type="button"
                  className="rounded font-medium text-info hover:underline focus-visible:ring-2 focus-visible:ring-info/30"
                  onClick={() => {
                    if (!reviewingOutliers) onReviewOutliers();
                    onDurationRange(
                      reviewingOutliers ? null : { min: outlierMin, max: Number.POSITIVE_INFINITY },
                    );
                  }}
                >
                  {reviewingOutliers
                    ? t("processPanel.histogramClearRange")
                    : t("processPanel.showOutliers")}
                </button>
              </output>
            )}
          </Tabs.Content>
          <Tabs.Content
            forceMount
            value="trend"
            className="col-start-1 row-start-1 self-start outline-none"
            style={{ visibility: chartView === "trend" ? "visible" : "hidden" }}
            aria-hidden={chartView !== "trend"}
          >
            {referenceTrend.length > 1 ? (
              <DurationTrendChart
                points={trend}
                referencePoints={referenceTrend}
                showTitle={false}
              />
            ) : (
              <p className="px-3 py-10 text-center text-sm text-text-dim">
                {t("processPanel.trendNeedsCases")}
              </p>
            )}
          </Tabs.Content>
        </div>
      </Tabs.Root>
    </PanelSection>
  );
}
